import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription, filter, BehaviorSubject } from "rxjs";

import { ProductEnvironment } from "@hermes/api-model-product-environment";
import { Context, Settings } from "@hermes/app-core";

import { AgecHeadService } from "../../head/agec-head.service";
import { AgecPageAnalyticsService } from "../../services/agec-page-analytics.service";
import { AgecService } from "../../services/agec.service";
import { AgecSearchBarComponent } from "../agec-search-bar/agec-search-bar.component";
export const AGEC_IMAGE_PATH = "loi-agec-page.png";
export const AGEC_IMAGE_MOBILE_PATH = "loi-agec-page-mobile.png";

@Component({
    selector: "h-agec-page",
    templateUrl: "./agec-page.component.html",
    styleUrls: ["./agec-page.component.scss"],
})
export class AgecPageComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild("searchBar", { static: true })
    public searchBar!: AgecSearchBarComponent;

    @ViewChild("search")
    public searchElement!: ElementRef;

    public product: ProductEnvironment | undefined = undefined;
    public skuNotFound: boolean = false;
    public agecImageFullPath = `${this.settings.assetsUrlEditoImage}${AGEC_IMAGE_PATH}`;
    public agecImageFullMobilePath = `${this.settings.assetsUrlEditoImage}${AGEC_IMAGE_MOBILE_PATH}`;
    public agecImageSrcSet: Array<{ srcSet: string; mediaQuery: string }> = [
        {
            mediaQuery: "(max-width: 414px)",
            srcSet: `${this.agecImageFullMobilePath}?fit=wrap&wid=420`,
        },
        {
            mediaQuery: "(min-width: 415px) and (max-width: 767px)",
            srcSet: `${this.agecImageFullMobilePath}?fit=wrap&wid=769`,
        },
        {
            mediaQuery: "(min-width: 768px) and (max-width: 1024px)",
            srcSet: `${this.agecImageFullPath}?fit=wrap&wid=1024`,
        },
        {
            mediaQuery: "(min-width: 1025px) and (max-width: 1280px)",
            srcSet: `${this.agecImageFullPath}?fit=wrap&wid=1280`,
        },
        {
            mediaQuery: "(min-width: 1281px) and (max-width: 1920px)",
            srcSet: `${this.agecImageFullPath}?fit=wrap&wid=1920`,
        },
        {
            mediaQuery: "(min-width: 1921px)",
            srcSet: `${this.agecImageFullPath}?fit=wrap&wid=3840`,
        },
    ];

    private showSku$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private subscriptions: Subscription = new Subscription();

    constructor(
        private agecService: AgecService,
        private agecPageAnalyticsService: AgecPageAnalyticsService,
        private agecHeadService: AgecHeadService,
        private route: ActivatedRoute,
        private settings: Settings,
        private context: Context,
    ) {}

    public ngOnInit(): void {
        this.agecHeadService.addMetaTagsAgec();
        this.agecPageAnalyticsService.sendAgecPageViewEvent();
        this.searchSkuWhenParam();
    }

    public ngAfterViewInit() {
        if (this.context.isInBrowserMode()) {
            this.subscriptions.add(
                this.showSku$
                    .pipe(filter((sku) => sku === true))
                    .subscribe(() => {
                        setTimeout(() => {
                            this.searchElement.nativeElement.scrollIntoView();
                        }, 500);
                    }),
            );
        }
    }

    public searchSkuWhenParam(): void {
        this.subscriptions.add(
            this.route.paramMap.subscribe((parameterMap) => {
                const sku = parameterMap.get("sku");
                if (sku) {
                    this.showSku$.next(true);
                    this.onFormSubmit(sku);
                    this.searchBar.setSearchBarValue(sku);
                }
            }),
        );
    }

    public onFormSubmit(productSKU: string): void {
        this.subscriptions.add(
            this.agecService.getProductBySKU(productSKU).subscribe({
                next: (product) => {
                    this.skuNotFound = false;
                    this.product = product;
                    this.agecPageAnalyticsService.sendVirtualPageViewEvent();
                    this.agecPageAnalyticsService.sendUAEvent({
                        eventaction: "Result",
                        eventlabel: productSKU,
                    });
                },
                error: () => {
                    this.skuNotFound = true;
                    this.product = undefined;
                    this.agecPageAnalyticsService.sendUAEvent({
                        eventaction: "Error",
                        eventlabel: productSKU,
                    });
                },
            }),
        );
    }

    public ngOnDestroy(): void {
        this.agecHeadService.removeMetaTagsAgec();
        this.subscriptions.unsubscribe();
    }
}
